import type { GroupSpec } from '@vitesse/statechart-host';

/*
 * WHAT A CONNECTED PATH IS, decided in one place.
 *
 * model-runner subscribes a model to the paths its wiring connects that start `controllers/` or
 * `models/`, and to nothing else, so this is not a cosmetic classification: it is the difference
 * between a connection and a silence. The panel that draws the wiring and anything that checks it
 * answer the question here, so they cannot disagree about which connections are real.
 */
export type BindingTarget =
  | { kind: 'controller'; controllerId: string; symbol: string }
  | { kind: 'model'; modelId: string; variable: string }
  | { kind: 'unresolvable' };

export function classifyBindingTarget(path: string): BindingTarget {
  const c = path.match(/^controllers\/([^/]+)\/symbols\/(.+)$/);
  if (c) return { kind: 'controller', controllerId: c[1], symbol: c[2] };
  const m = path.match(/^models\/([^/]+)\/variables\/(.+)$/);
  if (m) return { kind: 'model', modelId: m[1], variable: m[2] };
  return { kind: 'unresolvable' };
}

/**
 * A path pointed at a different controller, the symbol untouched.
 *
 * The Simulate panel's "against" picker: a model wired to tank-01 replayed against tank-02 asks
 * the same logic about a different device, so ONLY the controller segment moves. A path that is
 * not a controller symbol (another model's output, a value that is no path) stays as it is,
 * because to change it would invent a path that the author never wrote. A swap to the controller
 * that the path already names is not a swap.
 */
export function rebindPath(path: string, against: Record<string, string>): string {
  const c = classifyBindingTarget(path);
  const swap = c.kind === 'controller' ? against[c.controllerId] : undefined;
  return c.kind === 'controller' && swap && swap !== c.controllerId ? `controllers/${swap}/symbols/${c.symbol}` : path;
}

/** A set of connections (name → path) pointed at different controllers, as `rebindPath` does. Returns a new object. */
export function rebindControllers(connections: Record<string, string>, against: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, path] of Object.entries(connections)) out[name] = rebindPath(path, against);
  return out;
}

/**
 * A group spec with its wiring pointed at different controllers, as `rebindPath` does: each input
 * of the root and of each child, and each PLC output, so that a swapped device is read from and
 * written to as one. Returns a new spec; `spec` is never changed.
 */
export function rebindSpec(spec: GroupSpec, against: Record<string, string>): GroupSpec {
  type Inputs = NonNullable<GroupSpec['children'][number]['inputs']>;
  type Outputs = NonNullable<GroupSpec['children'][number]['outputs']>;
  const inputs = (map: Inputs | undefined): Inputs | undefined =>
    map && Object.fromEntries(Object.entries(map).map(([port, b]) => [port, 'path' in b ? { path: rebindPath(b.path, against) } : b]));
  const outputs = (map: Outputs | undefined): Outputs | undefined =>
    map && Object.fromEntries(Object.entries(map).map(([port, o]) => [port, { ...o, path: rebindPath(o.path, against) }]));
  const wiring = spec.root.wiring;
  return {
    ...spec,
    root: {
      ...spec.root,
      ...(wiring
        ? {
            wiring: {
              ...(wiring.inputs ? { inputs: inputs(wiring.inputs) } : {}),
              ...(wiring.outputs ? { outputs: outputs(wiring.outputs) } : {}),
            },
          }
        : {}),
    },
    children: spec.children.map((c) => ({
      ...c,
      ...(c.inputs ? { inputs: inputs(c.inputs) } : {}),
      ...(c.outputs ? { outputs: outputs(c.outputs) } : {}),
    })),
  };
}

/** The controller ids a set of connections names, in its order, once each. */
export function boundControllerIds(connections: Record<string, string>): string[] {
  const out: string[] = [];
  for (const path of Object.values(connections)) {
    const c = classifyBindingTarget(path);
    if (c.kind === 'controller' && !out.includes(c.controllerId)) out.push(c.controllerId);
  }
  return out;
}
